import { notification } from "antd";
import {
  setAllUsers,
  setChannelMessages,
  setChatUsers,
  setIsAllUsersLoading,
  setIsChannelMessagesLoading,
  setIsChatUsersLoading,
  setSelectedUser,
  setReactionUsers,
  setPendingReaction,
  clearPendingReaction,
  setHasMoreMessages,
  setIsLoadingOlder,
  prependChannelMessages,
} from "../../store/slices/chatsSlice";
import {
  apiUrl,
  collection_ref,
  constant,
  message_type,
  roles as salonRoles,
} from "../../utils/constants";
import { signInWithCustomToken } from "firebase/auth";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  endBefore,
  getDoc,
  getDocs,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  runTransaction,
} from "firebase/firestore";
import { ToastCrossIcon } from "../../constants/svg";
import { auth, db } from "../../firebase";
import {
  GetAuthUserLocalStorage,
  GetTokenLocalStorage,
} from "../localStorage/localStorage";
import { shouldClearPendingReaction } from "../../utils/reactions";

export const isSuperAdmin =
  GetAuthUserLocalStorage()?.roles[0]?.id == salonRoles.super_admin;

export const isUserDeleted = (user, members) => {
  if (user?.id == 1) return false;
  const member = members.find(
    (member) => member.id.toString() === user?.id?.toString(),
  );
  // If member is not found return true else return false
  return !member;
};

export const isChatUserDeleted = (user, members) => {
  const isDeleted = isSuperAdmin
    ? user?.is_deleted
    : isUserDeleted(user, members);
  return isDeleted;
};

// Helper function to convert Firebase Timestamps to serializable values
export const convertFirebaseTimestamps = (data) => {
  if (!data) return data;

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => convertFirebaseTimestamps(item));
  }

  // Handle objects
  if (typeof data === "object") {
    // Check if it's a Firebase Timestamp
    if (data.seconds !== undefined && data.nanoseconds !== undefined) {
      return {
        seconds: data.seconds,
        nanoseconds: data.nanoseconds,
      };
    }

    // Recursively convert all object properties
    const converted = {};
    for (const key in data) {
      converted[key] = convertFirebaseTimestamps(data[key]);
    }
    return converted;
  }

  // Return primitive values as-is
  return data;
};

let authUser = GetAuthUserLocalStorage();
let subscribed = null;

// Number of messages loaded per page for chat thread lazy loading.
export const CHAT_MESSAGES_PAGE_SIZE = 20;

// Oldest QueryDocumentSnapshot currently in the live window, keyed by chat_id.
// Used as a cursor for loadOlderMessages() with endBefore().
const oldestDocByChatId = new Map();

// For the "deleted messages" branch, stores the deletion-marker thread doc's
// created_at so older-page fetches can stop at the marker.
// Shape: Map<chat_id, { markerId: string, markerCreatedAt: any }>
const deletionMarkerByChatId = new Map();

const clearMatchingPendingReactions = (messages, dispatch, getState) => {
  const state = getState();
  const pendingReactions = state.chats.pendingReactions;

  messages.forEach((message) => {
    const messagePending = pendingReactions[message.thread_id];
    if (!messagePending) return;

    Object.keys(messagePending).forEach((emoji) => {
      const emojiPending = messagePending[emoji];
      Object.keys(emojiPending).forEach((userId) => {
        const isAdding = emojiPending[userId];
        if (shouldClearPendingReaction(message, emoji, userId, isAdding)) {
          dispatch(
            clearPendingReaction({
              messageId: message.thread_id,
              emoji,
              userId,
            }),
          );
        }
      });
    });
  });
};

export const getAllUsers = async (dispatch, keyword = null, salon_id) => {
  salon_id =
    typeof authUser?.salon_id != "undefined" ? authUser?.salon_id : salon_id;

  if (typeof salon_id != "undefined") {
    try {
      dispatch(setIsAllUsersLoading(true));
      const usersCollectionRef = collection(db, collection_ref?.users);
      let userquerySnapshot;
      if (keyword && keyword.length > 3) {
        let queryData = query(
          usersCollectionRef,
          // where("salon_id", "==", salon_id),
          where("salon_ids", "array-contains", salon_id),
          where("name", "==", keyword),
        );

        userquerySnapshot = await getDocs(queryData);
      } else {
        let queryData = query(
          usersCollectionRef,
          // where("salon_id", "==", salon_id),
          where("salon_ids", "array-contains", salon_id),
        );
        userquerySnapshot = await getDocs(queryData);
      }

      const users = [];
      for (let j = 0; j < userquerySnapshot?.docs?.length; j++) {
        const doc = userquerySnapshot?.docs[j];
        const userData = doc.data();
        userData.id = doc.id;
        users.push(userData);
      }
      // Convert Firebase Timestamps before storing in Redux
      const sanitizedUsers = users.map((user) =>
        convertFirebaseTimestamps(user),
      );
      dispatch(setAllUsers(sanitizedUsers));
      dispatch(setIsAllUsersLoading(false));
    } catch (e) {
      notification.error({
        description: e.message,
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
      dispatch(setIsAllUsersLoading(false));
    }
  }
};

export const getFireBaseUserByID = async (id) => {
  const usersRef = collection(db, collection_ref?.users);
  const q = query(usersRef, where("id", "==", id.toString()));
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    const userDoc = querySnapshot.docs[0];
    return userDoc.data();
  }
  return false;
};

/** Persist salon membership on Firestore user docs so chat queries and member counts stay consistent. */
const mergeSalonIdsForFirebaseUser = (existingSalonIds) => {
  const u = GetAuthUserLocalStorage();
  const currentSalonId = u?.salon?.id ?? u?.salon_id;
  const nums = new Set(
    (existingSalonIds || [])
      .map((x) => Number(x))
      .filter((n) => !Number.isNaN(n)),
  );
  if (currentSalonId != null && currentSalonId !== "") {
    const n = Number(currentSalonId);
    if (!Number.isNaN(n)) nums.add(n);
  }
  return Array.from(nums);
};

export const updateFirebaseUser = async (user = null) => {
  let auth_id =
    typeof authUser?.id == "undefined"
      ? user?.id.toString()
      : authUser?.id.toString();
  const userDocRef = doc(db, collection_ref?.users, auth_id);
  const userData = (await getDoc(userDocRef)).data();
  if (!userData) {
    const usersCollection = doc(db, collection_ref?.users, auth_id);
    await setDoc(usersCollection, {
      name: authUser?.name,
      image: authUser?.image ? authUser?.image : "",
      online: true,
      id: authUser?.id.toString(),
      email: authUser?.email,
      position: authUser?.position_id,
      salon_ids: mergeSalonIdsForFirebaseUser(undefined),
    });
  } else {
    userData.online = true;
    userData.salon_ids = mergeSalonIdsForFirebaseUser(userData.salon_ids);
    if (user) {
      userData.name = user?.name;
      userData.image = user?.image ? user?.image : "";
      userData.position = user?.position_id;
    }
    await updateDoc(userDocRef, userData);
  }
};
export const updateOrganizationFirebase = async (user = null) => {
  const userDocRef = doc(db, collection_ref?.users, user.id);
  const userData = (await getDoc(userDocRef)).data();
  if (!userData) {
    const usersCollection = doc(db, collection_ref?.users, user.id);
    await setDoc(usersCollection, {
      name: user?.name,
      image: user?.image ? user?.image : "",
      online: false,
      id: user?.id.toString(),
      email: user?.email,
      position: user?.position_id,
    });
  } else {
    if (user) {
      userData.name = user?.name;
      userData.image = user?.image ? user?.image : "";
      userData.position = user?.position_id;
    }
    await updateDoc(userDocRef, userData);
  }
};
// Helper function to sort chats with pinned/starred chats first
const sortChatsWithPinnedFirst = (chats, currentUserId) => {
  const pinnedChats = [];
  const unpinnedChats = [];

  chats.forEach((chat) => {
    try {
      const pinnedUsers = chat.users_who_pinned_chat || [];
      const isPinned = pinnedUsers.includes(currentUserId);

      if (isPinned) {
        pinnedChats.push(chat);
      } else {
        unpinnedChats.push(chat);
      }
    } catch (e) {
      unpinnedChats.push(chat);
    }
  });

  // Sort both arrays by updated_at descending
  const compareByUpdatedAt = (a, b) => {
    const timestampA = a.updated_at;
    const timestampB = b.updated_at;

    if (!timestampA && !timestampB) return 0;
    if (!timestampA) return 1;
    if (!timestampB) return -1;

    // Handle Firebase Timestamp objects
    const timeA = timestampA?.seconds || timestampA?.toMillis?.() || timestampA;
    const timeB = timestampB?.seconds || timestampB?.toMillis?.() || timestampB;

    // Convert to comparable numbers
    const numA = typeof timeA === "number" ? timeA : timeA * 1000;
    const numB = typeof timeB === "number" ? timeB : timeB * 1000;

    return numB - numA; // Descending order
  };

  pinnedChats.sort(compareByUpdatedAt);
  unpinnedChats.sort(compareByUpdatedAt);

  return [...pinnedChats, ...unpinnedChats];
};

export const toggleStarChat = async (chatId, userId) => {
  try {
    const chatDocRef = doc(db, collection_ref?.chats, chatId);
    const chatDoc = await getDoc(chatDocRef);

    if (!chatDoc.exists()) {
      notification.error({
        description: "Chat not found",
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
      return false;
    }

    const chatData = chatDoc.data();
    const pinnedUsers = chatData.users_who_pinned_chat || [];
    const isPinned = pinnedUsers.includes(userId);

    if (isPinned) {
      // Unstar the chat
      await updateDoc(chatDocRef, {
        users_who_pinned_chat: arrayRemove(userId),
      });
      notification.success({
        message: "Chat unstarred",
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
    } else {
      // Star the chat
      await updateDoc(chatDocRef, {
        users_who_pinned_chat: arrayUnion(userId),
      });
      notification.success({
        message: "Chat starred",
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
    }

    return true;
  } catch (error) {
    console.error("Failed to update star status", error);
    notification.error({
      description: error.message || "Failed to update star status",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return false;
  }
};

export const getChatUserList = (dispatch, auth_id) => {
  dispatch(setIsChatUsersLoading(true));

  const usersCollectionRef = collection(db, collection_ref?.users);
  const usersQuery = query(usersCollectionRef);

  const chatsCollectionRef = collection(db, collection_ref?.chats);
  const chatsQuery = query(
    chatsCollectionRef,
    where("users_ids", "array-contains", auth_id),
    ...(isSuperAdmin
      ? []
      : [where("salon_id", "==", parseInt(authUser?.salon?.id))]),
  );

  // helper to merge snapshots
  let chatUnsub = null;
  let updateTimeout = null;
  let lastUpdateTime = 0;
  const DEBOUNCE_MS = 100; // Debounce rapid updates to prevent flicker

  const userUnsub = onSnapshot(usersQuery, (usersSnap) => {
    // re-create chat subscription only once (or re-use existing)
    if (!chatUnsub) {
      chatUnsub = onSnapshot(chatsQuery, async () => {
        // Debounce rapid updates to prevent flicker when sending messages
        const now = Date.now();
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }

        updateTimeout = setTimeout(async () => {
          // ---- fetch latest chat list ordered ----
          const chatQuery = query(
            chatsCollectionRef,
            where("users_ids", "array-contains", auth_id),
            ...(isSuperAdmin
              ? []
              : [where("salon_id", "==", parseInt(authUser?.salon?.id))]),
            orderBy("updated_at", "desc"),
          );
          const chatData = await getDocs(chatQuery);

          const chats = [];
          chatData?.docs?.forEach((doc) => {
            const data = doc.data();
            data.chat_id = doc.id;
            data.is_user_group = !!data?.is_user_group;
            const [del] =
              data.deleted_statuses?.filter((d) => d.id == auth_id) || [];
            if (!del?.is_deleted) chats.push(data);
          });

          const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

          chats.forEach((chat) => {
            const profiles = chat.users_ids
              .filter((id) => id != auth_id)
              .map((id) => users.find((u) => Number(u.id) === Number(id)))
              .filter(Boolean);
            chat.receiver_profile = profiles;
          });

          const sanitized = chats.map((c) => convertFirebaseTimestamps(c));

          // Sort chats with pinned/starred chats first
          const sortedChats = sortChatsWithPinnedFirst(sanitized, auth_id);

          dispatch(setChatUsers(sortedChats));
          dispatch(setIsChatUsersLoading(false));
          lastUpdateTime = Date.now();
        }, DEBOUNCE_MS);
      });
    }
  });

  // return cleanup combining both listeners
  return () => {
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }
    userUnsub && userUnsub();
    chatUnsub && chatUnsub();
  };
};
export const getUnreadCount = async (setMessageCount) => {
  authUser = GetAuthUserLocalStorage();
  if (authUser?.id) {
    const chatsCollectionRef = collection(db, collection_ref?.chats);
    const chatsQuery = query(
      chatsCollectionRef,
      where("users_ids", "array-contains", authUser?.id),
    );

    await onSnapshot(chatsQuery, async (querySnapshot) => {
      let unreadCount = 0;
      querySnapshot.forEach((doc) => {
        const chatData = doc.data();
        chatData.chat_id = doc.id;
        const filteredData = chatData?.read_statuses?.filter(
          (item) => item.id == authUser?.id,
        );
        if (filteredData.length > 0 && !filteredData[0].is_read) {
          ++unreadCount;
        }
      });
      setMessageCount(unreadCount);
    });
  }
};
// Compare two serialized Firestore timestamps ({seconds, nanoseconds}).
// Returns < 0 if a < b, > 0 if a > b, 0 if equal. Handles nullish values.
const compareCreatedAt = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  const as = a.seconds ?? 0;
  const bs = b.seconds ?? 0;
  if (as !== bs) return as - bs;
  return (a.nanoseconds ?? 0) - (b.nanoseconds ?? 0);
};

// Merge the live-window messages back into the current Redux data, preserving
// older-page messages previously prepended via loadOlderMessages(). Dedupes by
// thread_id; only keeps prev messages strictly older than the new live window.
const mergeLiveWindowWithOlderPages = (prevData, liveData) => {
  if (!Array.isArray(prevData) || prevData.length === 0) return liveData;
  if (!Array.isArray(liveData) || liveData.length === 0) return prevData;

  const liveIds = new Set(liveData.map((m) => m?.thread_id).filter(Boolean));
  const oldestLiveCreatedAt = liveData[0]?.created_at;
  const preserved = prevData.filter((m) => {
    if (!m?.thread_id) return false;
    if (liveIds.has(m.thread_id)) return false;
    return compareCreatedAt(m.created_at, oldestLiveCreatedAt) < 0;
  });
  return [...preserved, ...liveData];
};

export const getChannelMessages = async (
  selectedUser,
  unsubscribe,
  dispatch,
  auth_id = null,
  getState = null,
) => {
  auth_id = typeof authUser?.id == "undefined" ? auth_id : authUser?.id;
  if (subscribed) {
    subscribed();
  }
  try {
    const chatId = selectedUser?.chat_id;
    // Reset pagination bookkeeping for this chat switch.
    oldestDocByChatId.delete(chatId);
    deletionMarkerByChatId.delete(chatId);
    dispatch(setChannelMessages([]));
    dispatch(setHasMoreMessages(false));
    dispatch(setIsLoadingOlder(false));
    // check if current user deleted messages
    const [currUserDeletedStatuses] =
      selectedUser && selectedUser.deleted_statuses
        ? selectedUser.deleted_statuses.filter((item) => item?.id == auth_id)
        : [];
    const isCurrentUserDeletedMsgs = currUserDeletedStatuses?.is_deleted;
    // Find the id of last message deleted by current user
    const [currUserDeletedMsgsIds] =
      selectedUser && selectedUser.deleted_messages_ids
        ? selectedUser.deleted_messages_ids.filter(
            (item) => item?.id == auth_id,
          )
        : "";
    const lastMsgId = currUserDeletedMsgsIds?.deleted_message_id;
    // if current user deleted messages than find the info of the deleted messages
    if (lastMsgId != "") {
      // finding the details of last messages deleted by current user
      dispatch(setIsChannelMessagesLoading(true));
      dispatch(setChannelMessages([]));

      const messagesRef = collection(
        doc(db, collection_ref?.chats, chatId),
        collection_ref?.threads,
      );

      // Look up the deletion-marker doc's created_at so later pagination can
      // stop at (not past) the marker. If the marker doc no longer exists we
      // fall back to treating as no marker.
      let markerCreatedAt = null;
      try {
        const markerSnap = await getDoc(doc(messagesRef, lastMsgId));
        if (markerSnap.exists()) {
          const markerData = convertFirebaseTimestamps(markerSnap.data());
          markerCreatedAt = markerData?.created_at || null;
          deletionMarkerByChatId.set(chatId, {
            markerId: lastMsgId,
            markerCreatedAt,
          });
        }
      } catch (_e) {
        // non-fatal — continue without a marker (will behave like normal branch)
      }

      const sortedMessagesQuery = query(
        messagesRef,
        orderBy("created_at"),
        limitToLast(CHAT_MESSAGES_PAGE_SIZE),
      );

      subscribed = onSnapshot(sortedMessagesQuery, (querySnapshot) => {
        const liveDataRaw = querySnapshot.docs.map((docSnap) => {
          const dt = convertFirebaseTimestamps(docSnap.data());
          dt.thread_id = docSnap.id;
          return dt;
        });

        // Filter out the marker itself and anything older than it.
        const liveData = markerCreatedAt
          ? liveDataRaw.filter(
              (m) =>
                m.thread_id !== lastMsgId &&
                compareCreatedAt(m.created_at, markerCreatedAt) > 0,
            )
          : liveDataRaw;

        // Track the oldest live doc snapshot for older-page cursor use.
        if (querySnapshot.docs.length > 0) {
          oldestDocByChatId.set(chatId, querySnapshot.docs[0]);
        } else {
          oldestDocByChatId.delete(chatId);
        }

        if (getState)
          clearMatchingPendingReactions(liveData, dispatch, getState);

        const prevData = getState
          ? getState().chats.channelMessages.data
          : null;
        const merged = mergeLiveWindowWithOlderPages(prevData, liveData);
        dispatch(setChannelMessages(merged));
        dispatch(setIsChannelMessagesLoading(false));

        // hasMore is true only when the live window is full AND the oldest
        // live doc is still strictly after the marker — otherwise we've
        // already reached the marker boundary.
        const windowFull = querySnapshot.docs.length >= CHAT_MESSAGES_PAGE_SIZE;
        const oldestLiveCreatedAt = liveDataRaw[0]?.created_at || null;
        const reachedMarker =
          markerCreatedAt &&
          (!oldestLiveCreatedAt ||
            compareCreatedAt(oldestLiveCreatedAt, markerCreatedAt) <= 0);
        dispatch(setHasMoreMessages(Boolean(windowFull && !reachedMarker)));
      });
    } else {
      // find all chat of current user
      dispatch(setIsChannelMessagesLoading(true));
      dispatch(setChannelMessages([]));

      const messagesRef = collection(
        doc(db, collection_ref?.chats, chatId),
        collection_ref?.threads,
      );
      const sortedMessagesQuery = query(
        messagesRef,
        orderBy("created_at"),
        limitToLast(CHAT_MESSAGES_PAGE_SIZE),
      );

      subscribed = onSnapshot(sortedMessagesQuery, (querySnapshot) => {
        const liveData = querySnapshot.docs.map((docSnap) => {
          const dt = convertFirebaseTimestamps(docSnap.data());
          dt.thread_id = docSnap.id;
          return dt;
        });

        // Track the oldest live doc snapshot for older-page cursor use.
        if (querySnapshot.docs.length > 0) {
          oldestDocByChatId.set(chatId, querySnapshot.docs[0]);
        } else {
          oldestDocByChatId.delete(chatId);
        }

        if (getState)
          clearMatchingPendingReactions(liveData, dispatch, getState);

        const prevData = getState
          ? getState().chats.channelMessages.data
          : null;
        const merged = mergeLiveWindowWithOlderPages(prevData, liveData);
        dispatch(setChannelMessages(merged));
        dispatch(setIsChannelMessagesLoading(false));
        dispatch(
          setHasMoreMessages(
            querySnapshot.docs.length >= CHAT_MESSAGES_PAGE_SIZE,
          ),
        );
      });
    }
    /* update unread to true */
    const chatsDocRef = doc(db, collection_ref?.chats, chatId);
    const chatData = (await getDoc(chatsDocRef)).data();
    const updatedArray = chatData?.read_statuses?.map((item) => {
      if (item.id == auth_id) {
        return {
          ...item,
          is_read: true,
        };
      } else {
        return item;
      }
    });
    if (updatedArray?.length > 0) {
      await updateDoc(chatsDocRef, {
        read_statuses: updatedArray,
      });
    }

    return subscribed;
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    dispatch(setIsChannelMessagesLoading(false));
  }
};

// Fetch an older page of messages for the given chat. Uses the cached oldest
// live-window doc as an endBefore() cursor; prepends results into Redux and
// updates hasMore based on whether a full page was returned (and whether the
// deletion marker, if any, has been reached).
export const loadOlderMessages = async (selectedUser, dispatch, getState) => {
  const chatId = selectedUser?.chat_id;
  if (!chatId) return;
  const cursor = oldestDocByChatId.get(chatId);
  if (!cursor) return;

  const state = getState ? getState() : null;
  if (state?.chats?.channelMessages?.isLoadingOlder) return;

  try {
    dispatch(setIsLoadingOlder(true));

    const messagesRef = collection(
      doc(db, collection_ref?.chats, chatId),
      collection_ref?.threads,
    );
    const olderQuery = query(
      messagesRef,
      orderBy("created_at"),
      endBefore(cursor),
      limitToLast(CHAT_MESSAGES_PAGE_SIZE),
    );
    const snap = await getDocs(olderQuery);

    let olderData = snap.docs.map((docSnap) => {
      const dt = convertFirebaseTimestamps(docSnap.data());
      dt.thread_id = docSnap.id;
      return dt;
    });

    // For the deleted-messages branch, drop anything at or older than the marker.
    const marker = deletionMarkerByChatId.get(chatId);
    if (marker?.markerCreatedAt) {
      olderData = olderData.filter(
        (m) =>
          m.thread_id !== marker.markerId &&
          compareCreatedAt(m.created_at, marker.markerCreatedAt) > 0,
      );
    }

    if (olderData.length > 0) {
      if (getState)
        clearMatchingPendingReactions(olderData, dispatch, getState);
      dispatch(prependChannelMessages(olderData));
    }

    // Update the cursor to the new oldest doc (only if we actually moved).
    if (snap.docs.length > 0) {
      oldestDocByChatId.set(chatId, snap.docs[0]);
    }

    // hasMore: only if we got a full page AND, for the deleted branch, we
    // haven't reached the marker yet.
    const fullPage = snap.docs.length >= CHAT_MESSAGES_PAGE_SIZE;
    const newOldestCreatedAt = snap.docs[0]
      ? convertFirebaseTimestamps(snap.docs[0].data())?.created_at
      : null;
    const reachedMarker =
      marker?.markerCreatedAt &&
      (!newOldestCreatedAt ||
        compareCreatedAt(newOldestCreatedAt, marker.markerCreatedAt) <= 0);
    dispatch(setHasMoreMessages(Boolean(fullPage && !reachedMarker)));
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  } finally {
    dispatch(setIsLoadingOlder(false));
  }
};

export const deleteThread = async (
  selectedUser,
  channelMessages,
  dispatch,
  onDelete,
  setOnDelete,
) => {
  try {
    const chatDocRef = doc(db, collection_ref?.chats, selectedUser?.chat_id);
    const chatDoc = await getDoc(chatDocRef);
    const lastMessage = channelMessages[channelMessages?.length - 1];

    if (chatDoc.exists()) {
      const data = chatDoc.data();
      const deleted_statuses = data?.deleted_statuses?.map((status) => {
        if (status?.id == authUser?.id) {
          return {
            id: authUser?.id,
            is_deleted: true,
          };
        } else {
          return status;
        }
      });

      const deleted_messages_ids = data?.deleted_messages_ids?.map((msg) => {
        if (msg?.id == authUser?.id) {
          return {
            id: authUser?.id,
            deleted_message_id: lastMessage?.thread_id || "",
          };
        } else {
          return msg;
        }
      });

      await updateDoc(chatDocRef, {
        deleted_statuses,
        deleted_messages_ids,
        updated_at: new Date(),
      });
      let tempSelectedUser = { ...selectedUser };
      tempSelectedUser.deleted_statuses = deleted_statuses;
      tempSelectedUser.deleted_messages_ids = deleted_messages_ids;

      dispatch(setSelectedUser(null));
      dispatch(setChannelMessages([]));
      setOnDelete(onDelete + 1);
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const sendMessage = async (selectedUser, newMessage) => {
  try {
    const chatsDocRef = doc(db, collection_ref?.chats, selectedUser?.chat_id);
    const messagesRef = collection(chatsDocRef, collection_ref?.threads);
    let chatData = await getDoc(chatsDocRef);
    chatData = chatData.data();
    if (newMessage?.message_type == message_type.text) {
      if (newMessage?.text && newMessage?.text != "") {
        await addDoc(messagesRef, newMessage);
      }
    } else {
      await addDoc(messagesRef, newMessage);
    }
    let lastMessage = "";
    switch (newMessage?.message_type) {
      case message_type?.text:
        lastMessage = newMessage?.text;
        break;
      case message_type?.media:
      case message_type?.image:
      case message_type?.video:
      case message_type?.audio:
      case message_type?.voice_memo:
      case message_type?.pdf:
      case message_type?.media_collection:
        lastMessage = newMessage?.text || "Attachment";
        break;
      default:
        lastMessage = newMessage?.text || "Message";
        break;
    }
    let deleted_statuses = [];
    chatData?.users_ids.map((item) => {
      deleted_statuses.push({ id: item, is_deleted: false });
    });
    let read_statuses = [];
    chatData?.users_ids.map((item) => {
      read_statuses.push({ id: item, is_read: item == authUser?.id });
    });
    await updateDoc(chatsDocRef, {
      updated_at: newMessage?.created_at,
      last_message: lastMessage,
      deleted_statuses,
      read_statuses,
      salon_id: isSuperAdmin
        ? parseInt(selectedUser.salon_id)
        : parseInt(authUser?.salon?.id),
    });

    // Update last open time when user sends a message
    if (selectedUser?.chat_id && authUser?.id) {
      await updateLastOpenTime(selectedUser.chat_id, authUser.id);
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const isUserAlreadyChatWithEachOther = async (
  curr_user_id,
  selected_user_id,
  dispatch,
  allUsers,
) => {
  try {
    let isAlreadyChat = false;
    let channel = null;
    let shouldBreak = false;
    let thread = null;
    // finds docs in which any one from curr_user_id or selected_user_id exist
    const q = query(
      collection(db, collection_ref?.chats),
      where("users_ids", "array-contains-any", [
        curr_user_id,
        selected_user_id,
      ]),
      where("is_group_chat", "==", false),
    );

    // check if users_ids contain curr_user_id and selected_user_id and users_ids.length == 2 (for 1-1 chat)
    // it means users already chat with each other else users are not already chat with each other
    const querySnapshot = await getDocs(q);
    querySnapshot?.docs?.forEach((doc) => {
      if (shouldBreak) {
      } else {
        const data = doc?.data();
        data.chat_id = doc.id;
        if (
          data?.users_ids?.includes(curr_user_id) &&
          data?.users_ids?.includes(selected_user_id) &&
          data?.users_ids.length == 2
        ) {
          thread = doc.id;
          isAlreadyChat = true;
          channel = data;
          shouldBreak = true; // break the loop if user already chat with each other
        } else {
          isAlreadyChat = false;
        }
      }
    });

    if (isAlreadyChat) {
      const usersRef = collection(db, collection_ref?.users);
      const q = query(usersRef, where("id", "==", selected_user_id.toString()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        channel.receiver_profile = [convertFirebaseTimestamps(userDoc.data())];
        dispatch(setSelectedUser(convertFirebaseTimestamps(channel)));
      }
    } else {
      let salonId = authUser?.salon?.id;
      if (isSuperAdmin) {
        salonId = allUsers?.find(
          (user) => user.id == selected_user_id,
        )?.salon_id;
      }
      const chatRef = collection(db, collection_ref?.chats);
      const addedDocRef = await addDoc(chatRef, {
        salon_id: salonId,
        blocked_statuses: [
          { id: curr_user_id, is_blocked: false },
          { id: selected_user_id, is_blocked: false },
        ],
        created_at: serverTimestamp(),
        deleted_messages_ids: [
          {
            deleted_message_id: "",
            id: curr_user_id,
            is_group_message_deleted: false,
          },
          {
            deleted_message_id: "",
            id: selected_user_id,
            is_group_message_deleted: false,
          },
        ],
        deleted_statuses: [
          { id: curr_user_id, is_deleted: false },
          { id: selected_user_id, is_deleted: false },
        ],
        is_group_chat: false,
        is_mobile_group: false,
        last_message: "",
        read_statuses: [
          { id: curr_user_id, is_read: false },
          { id: selected_user_id, is_read: false },
        ],
        updated_at: serverTimestamp(),
        users_ids: [curr_user_id, selected_user_id],
        group_message_statuses: [
          { id: curr_user_id, is_group_message_deleted: false },
          { id: selected_user_id, is_group_message_deleted: false },
        ],
      });
      const newChatDocSnapshot = await getDoc(addedDocRef);
      const newChatData = newChatDocSnapshot.data();
      newChatData.chat_id = newChatDocSnapshot.id;
      thread = newChatDocSnapshot.id;
      isAlreadyChat = false;

      // find selected user profile
      const usersRef = collection(db, collection_ref?.users);
      const q = query(usersRef, where("id", "==", selected_user_id.toString()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        newChatData.receiver_profile = [
          convertFirebaseTimestamps(userDoc.data()),
        ];
        dispatch(setSelectedUser(convertFirebaseTimestamps(newChatData)));
      }
    }
    return thread;
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const addUserToFirebase = async (data) => {
  try {
    await setDoc(doc(db, collection_ref?.users, data.id), data);
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const addUserToFirebaseOrReject = async (data) => {
  try {
    await Promise.race([
      setDoc(doc(db, collection_ref?.users, data.id), data),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), 20000),
      ),
    ]);
  } catch (e) {
    throw new Error(e.message);
  }
};

export const updateOnlineStatusOfLoginUser = async (user_id, status) => {
  try {
    const userDocRef = doc(db, collection_ref?.users, user_id?.toString());
    const docSnapshot = await getDoc(userDocRef);

    if (docSnapshot.exists()) {
      await updateDoc(userDocRef, { online: status });
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};
const normalizeMemberIds = (values) => {
  const seen = new Set();
  const ids = [];
  (values || []).forEach((value) => {
    const id = Number(value);
    if (!Number.isFinite(id) || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
};

const statusForMember = (rows, id) =>
  (rows || []).find((row) => Number(row?.id) === id);

const buildMemberFields = (chatData, { add = [], remove = [] } = {}) => {
  const removeSet = new Set(normalizeMemberIds(remove));
  const users = normalizeMemberIds([
    ...(chatData?.users_ids || []),
    ...add,
  ]).filter((id) => !removeSet.has(id));

  return {
    users_ids: users,
    blocked_statuses: users.map((id) => ({
      id,
      is_blocked:
        statusForMember(chatData?.blocked_statuses, id)?.is_blocked === true,
    })),
    deleted_statuses: users.map((id) => ({
      id,
      is_deleted:
        statusForMember(chatData?.deleted_statuses, id)?.is_deleted === true,
    })),
    deleted_messages_ids: users.map((id) => {
      const existing =
        statusForMember(chatData?.deleted_messages_ids, id) ||
        statusForMember(chatData?.deleted_messages_id, id);
      return {
        id,
        deleted_message_id: existing?.deleted_message_id || "",
        is_group_message_deleted: existing?.is_group_message_deleted === true,
      };
    }),
    group_message_statuses: users.map((id) => ({
      id,
      is_group_message_deleted:
        statusForMember(chatData?.group_message_statuses, id)
          ?.is_group_message_deleted === true,
    })),
    read_statuses: users.map((id) => {
      const existing = statusForMember(chatData?.read_statuses, id);
      if (existing) {
        return { id, is_read: existing.is_read === true };
      }
      return { id, is_read: true };
    }),
  };
};

const getChatsByGroupId = async (groupId) => {
  const chatsCollectionRef = collection(db, collection_ref?.chats);
  const snaps = [];
  const numericId = Number(groupId);
  if (Number.isFinite(numericId)) {
    snaps.push(
      await getDocs(
        query(chatsCollectionRef, where("group_id", "==", numericId)),
      ),
    );
  }
  snaps.push(
    await getDocs(
      query(chatsCollectionRef, where("group_id", "==", String(groupId))),
    ),
  );
  const byId = new Map();
  snaps.forEach((snap) => {
    snap.docs.forEach((chatDoc) => byId.set(chatDoc.id, chatDoc));
  });
  return [...byId.values()];
};

const groupChatCreates = new Map();

// Membership itself is updated by the API. This only posts the system line,
// so a desktop add cannot replace users_ids with a partial list.
export const syncFirebaseGroup = async (
  group_id,
  _group,
  newMessage = null,
) => {
  if (!newMessage || group_id == null) return;
  const chatDocs = await getChatsByGroupId(group_id);
  for (const chatDoc of chatDocs) {
    const chatsDocRef = doc(db, collection_ref?.chats, chatDoc.id);
    const messagesRef = collection(chatsDocRef, collection_ref?.threads);
    await addDoc(messagesRef, newMessage);
    await updateDoc(chatsDocRef, {
      updated_at: newMessage?.created_at || serverTimestamp(),
      last_message: newMessage?.text || "Message",
    });
  }
};
async function ensureGroupChatDocument(data) {
  const groupId = Number(data?.group_id);
  if (!Number.isFinite(groupId)) {
    throw new Error("Missing group id");
  }

  const key = String(groupId);
  if (groupChatCreates.has(key)) {
    return groupChatCreates.get(key);
  }

  const createPromise = (async () => {
    const existing = await getChatsByGroupId(groupId);
    if (existing.length > 0) return existing[0].id;

    const adminId = Number(authUser?.id);
    const salonId = Number.parseInt(authUser?.salon?.id, 10);
    if (!Number.isFinite(adminId) || !Number.isFinite(salonId)) {
      throw new Error("Missing user");
    }

    const users = normalizeMemberIds([...(data?.users || []), adminId]);
    const added = await addDoc(collection(db, collection_ref?.chats), {
      salon_id: salonId,
      group_id: groupId,
      admin_id: adminId,
      blocked_statuses: users.map((item) => ({
        id: item,
        is_blocked: false,
      })),
      created_at: serverTimestamp(),
      deleted_messages_ids: users.map((item) => ({
        deleted_message_id: "",
        id: item,
        is_group_message_deleted: false,
      })),
      deleted_statuses: users.map((item) => ({
        id: item,
        is_deleted: false,
      })),
      group_message_statuses: users.map((item) => ({
        id: item,
        is_group_message_deleted: false,
      })),
      image: data?.image ? data.image : "",
      is_group_chat: true,
      name: data?.name,
      last_message: "",
      read_statuses: users.map((item) => ({
        id: item,
        is_read: item === adminId,
      })),
      un_read_count: 0,
      updated_at: serverTimestamp(),
      users_ids: users,
    });
    return added.id;
  })();

  groupChatCreates.set(key, createPromise);
  try {
    return await createPromise;
  } finally {
    groupChatCreates.delete(key);
  }
}

export const createGroupChat = async (data, user = null) => {
  authUser = user || GetAuthUserLocalStorage() || authUser;
  if (!authUser) return;
  try {
    await ensureGroupChatDocument(data);
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const updateGroupChat = async (data) => {
  try {
    const chatDocs = await getChatsByGroupId(data?.group_id);
    for (const chatDoc of chatDocs) {
      await updateDoc(doc(db, collection_ref?.chats, chatDoc.id), {
        image: data?.image,
        name: data?.name,
      });
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const deleteGroupChat = async (group_id) => {
  try {
    const chatDocs = await getChatsByGroupId(group_id);
    for (const chatDoc of chatDocs) {
      await deleteDoc(doc(db, collection_ref?.chats, chatDoc.id));
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

const openGroupChatDocument = async (chatDoc, dispatch) => {
  const documentData = chatDoc.data();
  documentData.chat_id = chatDoc.id;

  const userquerySnapshot = await getDocs(
    collection(db, collection_ref?.users),
  );
  const users = [];
  userquerySnapshot.forEach((userDoc) => {
    const userData = userDoc.data();
    userData.id = userDoc.id;
    users.push(userData);
  });

  const profiles = [];
  for (let i = 0; i < documentData?.users_ids?.length; i++) {
    const [receiver_profile] = users.filter(
      (item) => item?.id == documentData?.users_ids[i],
    );
    profiles.push(receiver_profile);
  }

  documentData.receiver_profile = profiles.map((profile) =>
    convertFirebaseTimestamps(profile),
  );
  dispatch(setSelectedUser(convertFirebaseTimestamps(documentData)));
};

const pickGroupChatDoc = (docs) => {
  const authId = Number(GetAuthUserLocalStorage()?.id ?? authUser?.id);
  return (
    docs.find((chatDoc) =>
      (chatDoc.data()?.users_ids || []).some((id) => Number(id) === authId),
    ) || docs[0]
  );
};

export const redirectToGroupChat = async (
  group_id,
  dispatch,
  _usersMembers = [],
  group = null,
) => {
  authUser = GetAuthUserLocalStorage() || authUser;
  let chatDocs = await getChatsByGroupId(group_id);
  if (chatDocs.length === 0) {
    await createGroupChat(
      {
        group_id,
        users: (group?.users || []).map((item) => item.id),
        name: group?.name,
        image: group?.image ? group.image : "",
      },
      null,
    );
    chatDocs = await getChatsByGroupId(group_id);
  }
  if (chatDocs.length === 0) return;
  await openGroupChatDocument(pickGroupChatDoc(chatDocs), dispatch);
};

export const signInToFirebase = () => {
  let token = GetTokenLocalStorage();
  fetch(`${constant.BASE_URL + apiUrl.firebase_token}`)
    .then((response) => response.json())
    .then((data) => {
      if (data?.data && typeof data?.data === "string") {
        signInWithCustomToken(auth, data.data)
          .then(function (result) {
            let user = result.user;
          })
          .catch(function (error) {
            let errorCode = error.code;
            let errorMessage = error.message;
            console.log(errorMessage, "err msg");
          });
      }
    })
    .catch((error) => {
      console.error("Error:", error);
    });
};
export const signInToFirebaseSync = async () => {
  try {
    const response = await fetch(
      `${constant.BASE_URL + apiUrl.firebase_token}`,
    );
    const data = await response.json();

    if (data?.data && typeof data?.data === "string") {
      await signInWithCustomToken(auth, data.data);
      console.log("User successfully signed in to Firebase");
    }
  } catch (error) {
    throw new Error(`Firebase sign-in error: ${error.message}`);
  }
};
export const deleteFirebaseUser = async (user_id) => {
  user_id = user_id.toString();
  const userDocRef = doc(db, collection_ref?.users, user_id);
  const userData = (await getDoc(userDocRef)).data();

  if (userData) {
    userData.is_deleted = true;
    userData.name = userData?.name;
    userData.image = userData?.image ? userData?.image : "";
    userData.position = userData?.position_id || null;
    await updateDoc(userDocRef, userData);
  }
};

export const addReaction = async (
  messageId,
  chatId,
  emoji,
  userId,
  dispatch,
  message,
) => {
  try {
    const currentReactions = message.reactions || {};
    const hasReaction = currentReactions[emoji]?.users?.[userId];
    const isAdding = !hasReaction;

    dispatch(setPendingReaction({ messageId, emoji, userId, isAdding }));

    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      messageId,
    );

    await runTransaction(db, async (transaction) => {
      const messageDoc = await transaction.get(messageRef);

      if (!messageDoc.exists()) {
        throw new Error("Message does not exist!");
      }

      const messageData = messageDoc.data();
      const reactions = messageData.reactions || {};

      if (!reactions[emoji]) {
        reactions[emoji] = {
          count: 0,
          users: {},
        };
      }

      if (reactions[emoji].users[userId]) {
        delete reactions[emoji].users[userId];
        reactions[emoji].count = Math.max(0, reactions[emoji].count - 1);

        if (reactions[emoji].count === 0) {
          delete reactions[emoji];
        }
      } else {
        reactions[emoji].users[userId] = true;
        reactions[emoji].count = (reactions[emoji].count || 0) + 1;
      }

      transaction.update(messageRef, { reactions });
    });
  } catch (error) {
    dispatch(clearPendingReaction({ messageId, emoji, userId }));

    notification.error({
      description: error.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const getReactionUsers = async (messageId, chatId, emoji, dispatch) => {
  try {
    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      messageId,
    );
    const messageDoc = await getDoc(messageRef);

    if (messageDoc.exists()) {
      const messageData = messageDoc.data();
      const reactionData = messageData.reactions?.[emoji];

      if (reactionData?.users) {
        const userIds = Object.keys(reactionData.users);
        const users = [];

        // Fetch user details for each user who reacted
        for (const userId of userIds) {
          const userRef = doc(db, collection_ref?.users, userId);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            users.push(userDoc.data());
          }
        }

        dispatch(setReactionUsers({ messageId, emoji, users }));
        return users;
      }
    }

    return [];
  } catch (error) {
    notification.error({
      description: error.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return [];
  }
};

export const deleteMessage = async (message, chatId, dispatch) => {
  try {
    if (!message?.thread_id || !chatId) {
      throw new Error("Invalid message or chat ID");
    }

    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      message.thread_id,
    );

    await deleteDoc(messageRef);

    notification.success({
      description: "Message deleted successfully",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  } catch (error) {
    console.error("Error deleting message:", error);
    notification.error({
      description: error.message || "Failed to delete message",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const updateMessage = async (message, chatId, newText, dispatch) => {
  try {
    if (!message?.thread_id || !chatId) {
      throw new Error("Invalid message or chat ID");
    }

    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      message.thread_id,
    );

    await updateDoc(messageRef, {
      text: newText,
      edited: true,
      edited_at: serverTimestamp(),
    });

    notification.success({
      description: "Message updated successfully",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  } catch (error) {
    console.error("Error updating message:", error);
    notification.error({
      description: error.message || "Failed to update message",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const addMembersToGroup = async (chatId, newMemberIds, dispatch) => {
  try {
    const chatRef = doc(db, collection_ref?.chats, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error("Chat not found");
    }

    const chatData = chatDoc.data();
    authUser = GetAuthUserLocalStorage() || authUser;
    const salonId = Number.parseInt(authUser?.salon?.id, 10);
    const update = {
      ...buildMemberFields(chatData, { add: newMemberIds }),
      updated_at: serverTimestamp(),
    };
    if (Number.isFinite(salonId)) {
      update.salon_id = salonId;
    }

    await updateDoc(chatRef, update);

    // Update group message deleted status for all members
    await updateGroupMessageDeleted(chatId);

    getChatUserList(dispatch, GetAuthUserLocalStorage()?.id);

    return true;
  } catch (error) {
    console.error("Error adding members:", error);
    notification.error({
      description: "Failed to add members to group",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return false;
  }
};

export const removeMembersFromGroup = async (
  chatId,
  memberIdsToRemove,
  dispatch,
) => {
  try {
    const chatRef = doc(db, collection_ref?.chats, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error("Chat not found");
    }

    const chatData = chatDoc.data();
    authUser = GetAuthUserLocalStorage() || authUser;
    const salonId = Number.parseInt(authUser?.salon?.id, 10);
    const update = {
      ...buildMemberFields(chatData, { remove: memberIdsToRemove }),
      updated_at: serverTimestamp(),
    };
    if (Number.isFinite(salonId)) {
      update.salon_id = salonId;
    }

    await updateDoc(chatRef, update);

    // Update group message deleted status for remaining members
    await updateGroupMessageDeleted(chatId);
    getChatUserList(dispatch, GetAuthUserLocalStorage()?.id);

    return true;
  } catch (error) {
    console.error("Error removing members:", error);
    notification.error({
      description: "Failed to remove members from group",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return false;
  }
};

const updateGroupMessageDeleted = async (documentId) => {
  try {
    const chatDoc = await getDoc(doc(db, collection_ref?.chats, documentId));

    if (!chatDoc.exists()) return;

    const chatData = chatDoc.data();
    const groupMessageStatuses =
      chatData.group_message_statuses?.map((status) => ({
        id: status.id,
        is_group_message_deleted: false,
      })) || [];

    await updateDoc(doc(db, collection_ref?.chats, documentId), {
      updated_at: serverTimestamp(),
      group_message_statuses: groupMessageStatuses,
    });
  } catch (error) {
    console.error("Error updating group message deleted status:", error);
  }
};

// Debounce timer for last open time updates
let lastOpenTimeDebounceTimer = null;

/**
 * Updates the last open time for a user in a specific chat
 * This is called when a user opens/clicks on a chat item
 * Uses debouncing to prevent excessive Firestore writes
 */
export const updateLastOpenTime = async (chatId, userId) => {
  if (!chatId || !userId) return;

  // Clear existing debounce timer
  if (lastOpenTimeDebounceTimer) {
    clearTimeout(lastOpenTimeDebounceTimer);
  }

  // Set new debounce timer (1 second delay)
  lastOpenTimeDebounceTimer = setTimeout(async () => {
    try {
      const chatDocRef = doc(db, collection_ref?.chats, chatId);

      // Use Firestore transaction to prevent race conditions
      await runTransaction(db, async (transaction) => {
        const chatDoc = await transaction.get(chatDocRef);

        if (!chatDoc.exists()) {
          throw new Error("Chat document does not exist");
        }

        const chatData = chatDoc.data();
        const currentTime = new Date(); // Use regular Date instead of serverTimestamp()

        // Get existing last_open_time array or create new one
        let lastOpenTimeArray = chatData.last_open_time || [];

        // Find existing entry for this user
        const existingUserIndex = lastOpenTimeArray.findIndex(
          (entry) => entry.id === userId,
        );

        if (existingUserIndex !== -1) {
          // Update existing entry
          lastOpenTimeArray[existingUserIndex] = {
            id: userId,
            last_open_time: currentTime,
          };
        } else {
          // Add new entry
          lastOpenTimeArray.push({
            id: userId,
            last_open_time: currentTime,
          });
        }

        // Update the document
        transaction.update(chatDocRef, {
          last_open_time: lastOpenTimeArray,
        });
      });
    } catch (error) {
      console.error("Error updating last open time:", error);
    }
  }, 1000); // 1 second debounce
};

/**
 * Calculates which users have seen a specific message
 * This is called dynamically to determine seen_members for each message
 */
export const calculateSeenMembers = (message, chatData) => {
  if (!message || !chatData?.last_open_time) {
    return [];
  }

  // Handle different timestamp formats
  let messageTime;
  if (message.created_at?.toDate) {
    // Firebase Timestamp object
    messageTime = message.created_at.toDate();
  } else if (message.created_at?.seconds) {
    // Firebase serverTimestamp object
    messageTime = new Date(message.created_at.seconds * 1000);
  } else if (typeof message.created_at === "number") {
    // Unix timestamp
    messageTime = new Date(message.created_at);
  } else {
    // Try to parse as date string
    messageTime = new Date(message.created_at);
  }

  // Validate the date
  if (isNaN(messageTime.getTime())) {
    console.warn("Invalid message timestamp:", message.created_at);
    return [];
  }

  const seenMembers = [];

  chatData.last_open_time.forEach((userOpenTime) => {
    let userOpenTimeDate;
    if (userOpenTime.last_open_time?.toDate) {
      // Firebase Timestamp object
      userOpenTimeDate = userOpenTime.last_open_time.toDate();
    } else if (userOpenTime.last_open_time?.seconds) {
      // Firebase serverTimestamp object
      userOpenTimeDate = new Date(userOpenTime.last_open_time.seconds * 1000);
    } else if (typeof userOpenTime.last_open_time === "number") {
      // Unix timestamp
      userOpenTimeDate = new Date(userOpenTime.last_open_time);
    } else {
      // Try to parse as date string
      userOpenTimeDate = new Date(userOpenTime.last_open_time);
    }

    // Validate the user open time date
    if (!isNaN(userOpenTimeDate.getTime())) {
      // If user opened the chat after the message was sent, they have seen it
      if (userOpenTimeDate >= messageTime) {
        seenMembers.push(userOpenTime.id);
      }
    }
  });

  return seenMembers;
};
